import type { GroupedBalancesInfoModel } from 'api/models.v2';
import type { PaymentAccountResourceModel, PaymentMethodFee } from 'api/models';

export function getBalanceIds(balances: GroupedBalancesInfoModel[]): string[] {
  const ids = new Set<string>();

  for (const balance of balances) {
    if (balance.sourceType === 'GroupKey') {
      balance.balances?.forEach((item) => {
        if (item.id) {
          ids.add(item.id);
        }
      });
    } else if (balance.sourceType === 'Balance' && balance.sourceKey) {
      ids.add(balance.sourceKey);
    }
  }

  return Array.from(ids);
}

const MAINTENANCE_SOURCE_KEY_PREFIX = 'MAINTENANCE:';

export function isMaintenanceBalance(
  balance: GroupedBalancesInfoModel,
): boolean {
  if (
    balance.sourceType === 'GroupKey' &&
    balance.sourceKey?.includes(MAINTENANCE_SOURCE_KEY_PREFIX)
  ) {
    return true;
  }
  if (
    balance.sourceType === 'Balance' &&
    balance.groupKey?.includes(MAINTENANCE_SOURCE_KEY_PREFIX)
  ) {
    return true;
  }
  return false;
}

export function getMatchingFee(
  fees: PaymentMethodFee[],
  paymentMethod: PaymentAccountResourceModel,
): PaymentMethodFee | undefined {
  return fees.find(
    (fee) =>
      fee.paymentMethod === paymentMethod.paymentMethod &&
      ((fee.isDomestic && paymentMethod.isDomestic) ||
        (fee.isInternational && paymentMethod.isInternational)),
  );
}

export function shouldShowFee(
  paymentMethod: PaymentAccountResourceModel,
  matchingFee: PaymentMethodFee | undefined,
): boolean {
  const method = paymentMethod.paymentMethod;
  return (
    (method === 'Ach' || method === 'CreditCard') &&
    matchingFee != null &&
    matchingFee.feeAmount > 0
  );
}

export function getDefaultPaymentMethod(
  paymentMethods: PaymentAccountResourceModel[],
  isAllMaintenance: boolean,
  isHomeowner: boolean,
): PaymentAccountResourceModel | undefined {
  const findPrimary = (paymentType: string) =>
    paymentMethods.find((pm) =>
      pm.preferences?.some(
        (p) => p.paymentType === paymentType && p.priority === 'Primary',
      ),
    );

  if (isAllMaintenance) {
    const maintenancePrimary = findPrimary('Maintenance');
    if (maintenancePrimary) return maintenancePrimary;
  }

  if (isHomeowner) {
    const payoutPrimary = findPrimary('Payout');
    if (payoutPrimary) return payoutPrimary;
  }

  const rentPrimary = findPrimary('Rent');
  if (rentPrimary) return rentPrimary;

  return paymentMethods.find((pm) => pm.isVerified);
}

const PAY_IN_ADVANCE_MIN_DAYS = 10;

export function getPayInAdvanceBalances(
  balances: GroupedBalancesInfoModel[],
): GroupedBalancesInfoModel[] {
  const now = new Date();
  const advanceCutoff = new Date(
    now.getTime() + PAY_IN_ADVANCE_MIN_DAYS * 24 * 60 * 60 * 1000,
  );

  return balances.filter((b) => b.dueOn && new Date(b.dueOn) > advanceCutoff);
}

export function getFeeLabel(
  paymentMethod: 'Ach' | 'CreditCard' | 'Other' | 'Unknown',
): string {
  switch (paymentMethod) {
    case 'Ach':
      return 'ACH Fee';
    case 'CreditCard':
      return 'Credit Card Fee';
    default:
      return 'Payment Method Fee';
  }
}
